import { useState, useMemo } from 'react';  
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Building2,
  Calendar,
  Tag,
  Truck,
  Package,
  FileText,
  MessageCircle,
  Paperclip,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Phone,
  Mail,
  Copy,
  MapPin,
  Ticket,
  X,
  Users,
  Edit3
} from 'lucide-react';

import StatusBadge, { PriorityBadge } from '@components/common/StatusBadge';
import TicketComments from './TicketComments';
import TicketAttachments from './TicketAttachments';
import { useAuth } from '@context/AuthContext';
import { ticketService, userService } from '@services';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import { useUsers } from '@hooks/useUsers';
import toast from 'react-hot-toast';

const TicketDetails = ({ ticket = {} }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: '', reason: '' });
  const [assignForm, setAssignForm] = useState({ assignedTo: '', reason: '' });
  const [priorityForm, setPriorityForm] = useState({ priority: '', reason: '' });
  const { user, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { data: users = [] } = useUsers();

  const tabs = useMemo(
    () => [
      { id: 'details', label: 'Details', icon: FileText, count: 0 },
      { id: 'comments', label: 'Comments', icon: MessageCircle, count: ticket.comments?.length || 0 },
      { id: 'attachments', label: 'Attachments', icon: Paperclip, count: ticket.attachments?.length || 0 }
    ],
    [ticket.comments, ticket.attachments]
  );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const timeAgo = (dateString) => {
    if (!dateString) return '—';
    const now = new Date();
    const then = new Date(dateString);
    const diffMin = Math.floor((now - then) / (1000 * 60));
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return `${Math.floor(diffMin / 1440)}d ago`;
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (e) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Mutations for quick actions
  const statusMutation = useMutation(
    async ({ status, reason }) => {
      return await ticketService.updateTicketStatus(ticket.id, status, reason);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket', ticket.id]);
        queryClient.invalidateQueries(['tickets']);
        setShowStatusModal(false);
        setStatusForm({ status: '', reason: '' });
        toast.success('Status updated successfully');
      },
      onError: (error) => {
        console.error('Status update error:', error);
        toast.error('Failed to update status. Please try again.');
      }
    }
  );

  const assignMutation = useMutation(
    async ({ assignedTo, reason }) => {
      return await ticketService.assignTicket(ticket.id, assignedTo, reason);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket', ticket.id]);
        queryClient.invalidateQueries(['tickets']);
        setShowAssignModal(false);
        setAssignForm({ assignedTo: '', reason: '' });
        toast.success('Ticket reassigned successfully');
      },
      onError: (error) => {
        console.error('Assignment error:', error);
        toast.error('Failed to reassign ticket. Please try again.');
      }
    }
  );

  const priorityMutation = useMutation(
    async ({ priority, reason }) => {
      return await ticketService.updateTicket(ticket.id, { priority });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket', ticket.id]);
        queryClient.invalidateQueries(['tickets']);
        setShowPriorityModal(false);
        setPriorityForm({ priority: '', reason: '' });
        toast.success('Priority updated successfully');
      },
      onError: (error) => {
        console.error('Priority update error:', error);
        toast.error('Failed to update priority. Please try again.');
      }
    }
  );

  // Quick action handlers
  const handleStatusChange = (newStatus) => {
    setStatusForm({ status: newStatus, reason: '' });
    setShowStatusModal(true);
  };

  const handleReassign = () => {
    setAssignForm({ assignedTo: '', reason: '' });
    setShowAssignModal(true);
  };

  const handlePriorityChange = (newPriority) => {
    setPriorityForm({ priority: newPriority, reason: '' });
    setShowPriorityModal(true);
  };

  const submitStatusChange = () => {
    if (!statusForm.status) return;
    statusMutation.mutate(statusForm);
  };

  const submitReassign = () => {
    if (!assignForm.assignedTo) {
      toast.error('Please select a user to assign');
      return;
    }
    assignMutation.mutate(assignForm);
  };

  const submitPriorityChange = () => {
    if (!priorityForm.priority) return;
    priorityMutation.mutate(priorityForm);
  };

  // Permission checks
  const canEditStatus = hasPermission('can_edit') || user?.role === 'admin' || user?.role === 'master';
  const canAssignTickets = hasPermission('can_edit') || user?.role === 'admin' || user?.role === 'master';
  const canChangePriority = hasPermission('can_edit') || user?.role === 'admin' || user?.role === 'master';
  const canCloseTickets = hasPermission('can_delete') || user?.role === 'admin' || user?.role === 'master';

  // defensive shorthand
  const {
    subject = 'No subject',
    body = '',
    status,
    priority,
    created_at,
    updated_at,
    assigned_to,
    created_by,
    customer,
    supplier,
    module,
    category,
    subcategory,
    branch,
    shipment_number,
    order_number,
    vehicle_number,
    estimated_resolution,
    actual_resolution,
    status_history = []
  } = ticket;

  return (
    <div className="space-y-6">
      {/* Header Section - Clean and focused */}
      <motion.div
        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Top Header with status and actions */}
        <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-mono text-gray-600 bg-white px-3 py-1 rounded border">
                <Ticket className="w-4 h-4" />
                {ticket.ticket_number || '—'}
              </div>
              <StatusBadge status={status} />
              <PriorityBadge priority={priority} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setActiveTab('comments')}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">Comment</span>
              </button>
              <button
                onClick={() => setActiveTab('attachments')}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
              >
                <Paperclip className="w-4 h-4" />
                <span className="hidden sm:inline">Files</span>
              </button>
              {canEditStatus && (
                <div className="relative">
                  <select
                    value={status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
                    disabled={statusMutation.isLoading}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main header content */}
        <div className="p-6">
          <div className="mb-4">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">{subject}</h1>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Created </span>{timeAgo(created_at)}
              </div>
              {ticket?.branch && (
                <div className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  <span className="truncate max-w-48">
                    {ticket.branch.name}{ticket.branch.city ? `, ${ticket.branch.city}` : ''}
                  </span>
                </div>
              )}
              {assigned_to && (
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span className="truncate max-w-32">
                    {assigned_to.first_name} {assigned_to.last_name}
                  </span>
                </div>
              )}
            </div>
          </div>
                    
          {body && (
            <div className="prose max-w-none text-gray-700 bg-gray-50 rounded-lg p-4 border">
              {body}
            </div>
          )}
        </div>
      </motion.div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content Area */}
        <div className="lg:col-span-3 order-2 lg:order-1">
          {/* Tabs */}
          <motion.div
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.1 }}
          >
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="flex">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                        isActive 
                          ? 'border-blue-500 text-blue-600 bg-blue-50' 
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                          isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'details' && (
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6"
                >
                  <div className="space-y-4">
                    <DetailRow label="Module" icon={Tag}>
                      {module?.name || '—'}
                    </DetailRow>
                    <DetailRow label="Category" icon={Tag}>
                      {category?.name || '—'}
                    </DetailRow>
                    <DetailRow label="Subcategory" icon={Tag}>
                      {subcategory?.name || '—'}
                    </DetailRow>
                    <DetailRow label="Branch" icon={Building2}>
                      {branch ? `${branch.name}${branch.city ? ` — ${branch.city}` : ''}` : '—'}
                    </DetailRow>
                    <DetailRow label="Created" icon={Calendar}>
                      {formatDate(created_at)}
                    </DetailRow>
                    {updated_at && updated_at !== created_at && (
                      <DetailRow label="Last Updated" icon={Clock}>
                        {formatDate(updated_at)}
                      </DetailRow>
                    )}
                  </div>
                  <div className="space-y-4">
                    {shipment_number && (
                      <DetailRow label="Shipment Number" icon={Package} showCopy>
                        {shipment_number}
                      </DetailRow>
                    )}
                    {order_number && (
                      <DetailRow label="Order Number" icon={FileText} showCopy>
                        {order_number}
                      </DetailRow>
                    )}
                    {vehicle_number && (
                      <DetailRow label="Vehicle Number" icon={Truck} showCopy>
                        {vehicle_number}
                      </DetailRow>
                    )}
                    {estimated_resolution && (
                      <DetailRow label="Est. Resolution" icon={AlertTriangle}>
                        {formatDate(estimated_resolution)}
                      </DetailRow>
                    )}
                    {actual_resolution && (
                      <DetailRow label="Actual Resolution" icon={CheckCircle2}>
                        {formatDate(actual_resolution)}
                      </DetailRow>
                    )}
                  </div>
                </motion.div>
              )}

              {activeTab === 'comments' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TicketComments ticketId={ticket.id} comments={ticket.comments || []} />
                </motion.div>
              )}

              {activeTab === 'attachments' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TicketAttachments ticketId={ticket.id} attachments={ticket.attachments || []} />
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6 order-1 lg:order-2">
          {/* People */}
          <motion.div
            className="bg-white rounded-lg border border-gray-200 p-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-gray-600" />
              People
            </h3>
            <div className="space-y-4">
              <PersonRow label="Assigned to" person={assigned_to} fallback="Not assigned" />
              <PersonRow label="Created by" person={created_by} fallback="—" />
              <PersonRow label="Customer" person={customer} fallback="—" accent="blue" />
              <PersonRow label="Supplier" person={supplier} fallback="—" accent="orange" />
            </div>
          </motion.div>

          {/* Timeline */}
          <motion.div
            className="bg-white rounded-lg border border-gray-200 p-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600" />
              Timeline
            </h3>
            <div className="space-y-3">
              {status_history && status_history.length > 0 ? (
                status_history.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 bg-blue-500 rounded-full flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        Status changed to <span className="text-blue-600">{s.to_status}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {timeAgo(s.changed_at)} by {s.changed_by?.first_name || 'System'}
                        {s.reason && ` • ${s.reason}`}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 italic">No status changes yet</div>
              )}
            </div>
          </motion.div>

          {/* Quick Actions */}
          {user && (canEditStatus || canAssignTickets || canChangePriority) && (
            <motion.div
              className="bg-white rounded-lg border border-gray-200 p-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.4 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-gray-600" />
                Quick Actions
              </h3>
              <div className="space-y-2">
                {canEditStatus && (
                  <>
                    <button 
                      onClick={() => handleStatusChange('in_progress')}
                      disabled={status === 'in_progress' || statusMutation.isLoading}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                                            Mark In Progress
                    </button>
                    <button 
                      onClick={() => handleStatusChange('resolved')}
                      disabled={status === 'resolved' || statusMutation.isLoading}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                                            Mark Resolved
                    </button>
                  </>
                )}
                {canAssignTickets && (
                  <button 
                    onClick={handleReassign}
                    disabled={assignMutation.isLoading}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                                        Reassign ticket
                  </button>
                )}
                {canChangePriority && (
                  <div className="space-y-1">
                    <button 
                      onClick={() => handlePriorityChange('high')}
                      disabled={priority === 'high' || priorityMutation.isLoading}
                      className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                                            Set High Priority
                    </button>
                    <button 
                      onClick={() => handlePriorityChange('critical')}
                      disabled={priority === 'critical' || priorityMutation.isLoading}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                                            Set Critical Priority
                    </button>
                  </div>
                )}
                {(user?.role === 'admin' || user?.role === 'master') && (
                  <button 
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => {
                      if (confirm('Add this ticket to your watchlist?')) {
                        toast.success('Added to watchlist (feature coming soon)');
                      }
                    }}
                  >
                                        Add to watchlist
                  </button>
                )}
                <hr className="my-3" />
                {canCloseTickets && (
                  <button 
                    onClick={() => handleStatusChange('closed')}
                    disabled={status === 'closed' || statusMutation.isLoading}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                                        Close ticket
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* Master/Admin Analytics */}
          {(user?.role === 'admin' || user?.role === 'master') && (
            <motion.div
              className="bg-white rounded-xl border border-gray-200 p-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.5 }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-gray-600" />
                                Analytics
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Response Time</span>
                  <span className="font-medium">
                    {ticket.first_response_at 
                      ? `${Math.round((new Date(ticket.first_response_at) - new Date(created_at)) / (1000 * 60 * 60))}h`
                      : 'Pending'
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Time to Resolution</span>
                  <span className="font-medium">
                    {actual_resolution 
                      ? `${Math.round((new Date(actual_resolution) - new Date(created_at)) / (1000 * 60 * 60 * 24))}d`
                      : 'In Progress'
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Comments</span>
                  <span className="font-medium">{ticket.comments?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Attachments</span>
                  <span className="font-medium">{ticket.attachments?.length || 0}</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
            
      {/* Modals */}
      <StatusModal 
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        form={statusForm}
        setForm={setStatusForm}
        onSubmit={submitStatusChange}
        loading={statusMutation.isLoading}
        currentStatus={status}
      />
            
      <AssignModal 
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        form={assignForm}
        setForm={setAssignForm}
        onSubmit={submitReassign}
        loading={assignMutation.isLoading}
        users={users}
        currentAssignee={assigned_to}
      />
            
      <PriorityModal 
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        form={priorityForm}
        setForm={setPriorityForm}
        onSubmit={submitPriorityChange}
        loading={priorityMutation.isLoading}
        currentPriority={priority}
      />
    </div>
  );
};

/* --- Modal Components --- */

const StatusModal = ({ isOpen, onClose, form, setForm, onSubmit, loading, currentStatus }) => {
  const statusOptions = [
    { value: 'not_started', label: 'Not Started', color: 'gray' },
    { value: 'in_progress', label: 'In Progress', color: 'blue' },
    { value: 'on_hold', label: 'On Hold', color: 'yellow' },
    { value: 'completed', label: 'Completed', color: 'green' },
    { value: 'cancelled', label: 'Cancelled', color: 'red' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Status"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Status
          </label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            <option value="">Select status</option>
            {statusOptions
              .filter(option => option.value !== currentStatus)
              .map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            }
          </select>
        </div>
                
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason for Change
          </label>
          <textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Explain why you're changing the status..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
                
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
                        Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={!form.status}
          >
                        Update Status
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const AssignModal = ({ isOpen, onClose, form, setForm, onSubmit, loading, users, currentAssignee }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const availableUsers = users.filter(user => user.id !== currentAssignee?.id);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reassign Ticket"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assign to
          </label>
          <select
            value={form.assignedTo}
            onChange={(e) => setForm({ ...form, assignedTo: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            <option value="">Select user</option>
            {availableUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.first_name} {user.last_name} ({user.role})
              </option>
            ))}
          </select>
        </div>
                
        {currentAssignee && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
                            Currently assigned to: <span className="font-medium">
                {currentAssignee.first_name} {currentAssignee.last_name}
              </span>
            </p>
          </div>
        )}
                
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason for Reassignment
          </label>
          <textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Explain why you're reassigning this ticket..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
                
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
                        Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={!form.assignedTo}
          >
                        Reassign Ticket
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const PriorityModal = ({ isOpen, onClose, form, setForm, onSubmit, loading, currentPriority }) => {
  const priorityOptions = [
    { value: 'low', label: 'Low Priority', color: 'gray' },
    { value: 'medium', label: 'Medium Priority', color: 'yellow' },
    { value: 'high', label: 'High Priority', color: 'orange' },
    { value: 'critical', label: 'Critical Priority', color: 'red' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Priority"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Priority
          </label>
          <select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          >
            <option value="">Select priority</option>
            {priorityOptions
              .filter(option => option.value !== currentPriority)
              .map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            }
          </select>
        </div>
                
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason for Change
          </label>
          <textarea
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="Explain why you're changing the priority..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
                
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
                        Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={!form.priority}
          >
                        Update Priority
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/* --- small helper components used above --- */

const DetailRow = ({ label, icon: Icon, children, showCopy = false }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <label className="text-sm font-medium text-gray-500 block mb-2">{label}</label>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-400" />}
          <span className="text-gray-900 font-medium">{children}</span>
        </div>
        {showCopy && (
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(String(children))}
            className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
            aria-label={`Copy ${label}`}
          >
            <Copy className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const PersonRow = ({ label, person, fallback = '—', accent }) => {
  if (!person) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <label className="text-sm font-medium text-gray-500 block mb-2">{label}</label>
        <div className="text-sm text-gray-500 italic">{fallback}</div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <label className="text-sm font-medium text-gray-500 block mb-3">{label}</label>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          accent === 'blue' ? 'bg-blue-100' : 
            accent === 'orange' ? 'bg-orange-100' : 
              'bg-gray-100'
        }`}>
          <User className={`w-5 h-5 ${
            accent === 'blue' ? 'text-blue-600' : 
              accent === 'orange' ? 'text-orange-600' : 
                'text-gray-600'
          }`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {person.first_name} {person.last_name}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {person.role && <div className="capitalize">{person.role}</div>}
            {person.email && (
              <div className="flex items-center gap-1 mt-1">
                <Mail className="w-3 h-3" />
                <span className="truncate">{person.email}</span>
              </div>
            )}
            {person.phone && (
              <div className="flex items-center gap-1 mt-1">
                <Phone className="w-3 h-3" />
                <span>{person.phone}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetails;
